import React from "react";
import Grid from "@mui/material/Grid";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";
import { isRequired, postalCodeDefault, requiredValidator, streetAddressDefault } from "@/src/services/validationRules";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import { translate } from "@/src/services/translations/translations";
import { FormDataTypes } from "@/src/types/FormDataTypes";
import { useForm } from "@/src/provider/Form/FormProvider";
import wizardStyle from "@/src/components/Form/wizardStyle";
import Typography from "@mui/material/Typography";
import ValidatorGooglePlacesAutocomplete from "@/src/components/FormFields/ValidatorGooglePlacesAutocomplete";
import { GoogleMapsAddressResult } from "@/src/types/GoogleMapsAddressResult";
import { useCookieConsent } from "@/src/provider/CookieConsent/CookieConsentProvider";

type AddressProps = {
  path: string;
  headline?: string;
  info?: string | null;
  fieldsAreRequired?: boolean;
  disabled?: boolean;
};

const Address = ({ path, headline = "Wo wohnen Sie?", info = null, fieldsAreRequired = true, disabled = false }: AddressProps) => {
  const { values, updateValue }: FormDataTypes = useForm();
  const { hasConsent } = useCookieConsent();

  const handleAddressSelect = (address: GoogleMapsAddressResult) => {
    updateValue(`${path}.streetAddress`, address.streetAddress);
    updateValue(`${path}.postalCode`, address.postalCode);
    updateValue(`${path}.addressLocality`, address.addressLocality);
  };

  const hasAcceptedAnalyticsCookies = hasConsent("analytics");

  return (
    <>
      {headline && (
        <Grid sx={wizardStyle.gridTopSpaced} size={12}>
          <FormLegend>{headline}</FormLegend>
        </Grid>
      )}
      {info && (
        <Grid sx={{ textAlign: "center" }} size={12}>
          <Typography variant={"caption"}>{info}</Typography>
        </Grid>
      )}
      <Grid size={12}>
        {hasAcceptedAnalyticsCookies ? (
          <ValidatorGooglePlacesAutocomplete
            label={translate("address.streetAddress.label", values)}
            name={`${path}.streetAddress`}
            validators={fieldsAreRequired ? [requiredValidator("Bitte geben Sie die Straße und Hausnummer an"), streetAddressDefault] : [streetAddressDefault]}
            onAddressSelect={handleAddressSelect}
          />
        ) : (
          <ValidatorTextField
            name={`${path}.streetAddress`}
            label={translate("address.streetAddress.label", values)}
            disabled={disabled}
            validators={fieldsAreRequired ? [requiredValidator("Bitte geben Sie die Straße und Hausnummer an"), streetAddressDefault] : [streetAddressDefault]}
          />
        )}
      </Grid>
      <Grid
        sx={wizardStyle.gridSpace}
        size={{
          xs: 12,
          sm: 6
        }}>
        <ValidatorTextField
          name={`${path}.postalCode`}
          label={translate("address.postalCode.label", values)}
          disabled={disabled}
          validators={fieldsAreRequired ? [requiredValidator("Bitte geben Sie die Postleitzahl an"), postalCodeDefault] : [postalCodeDefault]}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6
        }}>
        <ValidatorTextField
          name={`${path}.addressLocality`}
          label={translate("address.addressLocality.label", values)}
          disabled={disabled}
          validators={fieldsAreRequired ? [{ validator: isRequired, message: "Bitte geben Sie den Wohnort an" }] : []}
        />
      </Grid>
    </>
  );
};

export default Address;
